import {
    AdminProjectSummary,
    CreateOrganizationRequest,
    Organization,
    OrganizationUser,
} from "../../types/AdminDashboardTypes";
import {
    DeliveryStatus,
    NotificationDelivery,
    NotificationDeliveryPage,
} from "../../types/NotificationTypes";
import { InviteUsersRequest, InviteUsersResponse, UserRole } from "../../types/UserGroup";
import { safeDELETE, safeGET, safePOST, safePUT } from "./SafeClient";

/**
 * Client for the internal operations dashboard (/superadmin/dashboard).
 * Dashboard features go through this client and its backend counterpart
 * (node-services-v2's AdminDashboardService), so the dashboard's surface
 * area stays in one place.
 */

export function listOrganizations(abort?: any): Promise<Organization[]> {
    return safeGET("/api/admin/organizations", abort);
}

export function createOrganization(request: CreateOrganizationRequest): Promise<Organization> {
    return safePOST("/api/admin/organizations", request);
}

export function getOrganization(orgId: number, abort?: any): Promise<Organization> {
    return safeGET(`/api/admin/organizations/${orgId}`, abort);
}

export function updateOrganization(
    orgId: number,
    update: { name?: string }
): Promise<Organization> {
    return safePUT(`/api/admin/organizations/${orgId}`, update);
}

export function listOrganizationUsers(orgId: number, abort?: any): Promise<OrganizationUser[]> {
    return safeGET(`/api/admin/organizations/${orgId}/users`, abort);
}

export function listOrganizationProjects(orgId: number): Promise<AdminProjectSummary[]> {
    return safeGET(`/api/admin/organizations/${orgId}/projects`);
}

/** Soft-deletes the project and everything that hangs off it. */
export function deleteProject(projectId: number): Promise<unknown> {
    return safeDELETE(`/api/admin/projects/${projectId}`, undefined);
}

export function inviteOrganizationUsers(
    orgId: number,
    request: InviteUsersRequest
): Promise<InviteUsersResponse> {
    return safePOST(`/api/admin/organizations/${orgId}/users/invite`, request);
}

export function resendInvitation(userId: number): Promise<OrganizationUser> {
    return safePOST(`/api/admin/users/${userId}/resend-invite`, undefined);
}

export function deactivateUser(userId: number): Promise<OrganizationUser> {
    return safePOST(`/api/admin/users/${userId}/deactivate`, undefined);
}

export function reactivateUser(userId: number): Promise<OrganizationUser> {
    return safePOST(`/api/admin/users/${userId}/reactivate`, undefined);
}

export function setUserRoles(userId: number, roles: UserRole[]): Promise<OrganizationUser> {
    return safePUT(`/api/admin/users/${userId}/roles`, { roles });
}

export function deactivateOrganization(orgId: number): Promise<Organization> {
    return safePOST(`/api/admin/organizations/${orgId}/deactivate`, undefined);
}

export function activateOrganization(orgId: number): Promise<Organization> {
    return safePOST(`/api/admin/organizations/${orgId}/activate`, undefined);
}

export function impersonateOrganization(orgId: number, abort?: any): Promise<unknown> {
    return safePOST(`/api/admin/organizations/${orgId}/impersonate`, undefined, abort);
}

export function stopImpersonating(abort?: any): Promise<unknown> {
    return safePOST("/api/admin/stop-impersonating", undefined, abort);
}

/**
 * Every organization's notification deliveries, newest first: `limit` of
 * them older than `before` (the `next` of the previous page), narrowed by
 * status and by a text the recipient, the organization or the project
 * matches.
 */
export function listNotificationDeliveries(
    limit: number,
    before?: number | null,
    status?: DeliveryStatus,
    search?: string,
    abort?: any
): Promise<NotificationDeliveryPage> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before != null) params.set("before", String(before));
    if (status) params.set("status", status);
    if (search) params.set("search", search);
    return safeGET(`/api/admin/notifications/deliveries?${params.toString()}`, abort);
}

/** Queue a delivery again, whatever its state. */
export function retryNotificationDelivery(id: number): Promise<NotificationDelivery> {
    return safePOST(`/api/admin/notifications/deliveries/${id}/retry`, {});
}
